import { forwardRef, useCallback, type ChangeEvent } from "react";

import { platformProps } from "../../lib";
import type { TextAreaPrimitiveProps } from "./types";

/**
 * TextArea Primitive - Web implementation
 *
 * Renders a native `<textarea>` element with unified prop interface.
 * Transforms `onChangeText` to standard `onChange` for web.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaPrimitiveProps>(
  (
    {
      className,
      testID,
      id,
      disabled,
      readOnly,
      invalid = false,
      rows,
      onChangeText,
      onFocus,
      onBlur,
      accessibilityLabel,
      // Native-only (the phone's text size); taken out so it doesn't reach the DOM
      maxFontSizeMultiplier: _maxFontSizeMultiplier,
      ...rest
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        onChangeText?.(e.target.value);
      },
      [onChangeText],
    );

    return (
      <textarea
        ref={ref}
        className={className}
        {...platformProps({ testID, id })}
        disabled={disabled}
        readOnly={readOnly}
        rows={rows}
        aria-label={accessibilityLabel}
        aria-invalid={invalid || undefined}
        aria-multiline="true"
        onChange={onChangeText ? handleChange : undefined}
        onFocus={onFocus ? () => onFocus() : undefined}
        onBlur={onBlur ? () => onBlur() : undefined}
        {...rest}
      />
    );
  },
);

TextArea.displayName = "TextArea";
